// ===== Imports =====
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReduxState} from '../../store/types';
import * as Yup from 'yup';
import {Formik} from 'formik';
import AiForm from '../Forms/Extensions/AiForm';
import {actions} from '../../store';
import {AiFormType} from '../Forms/Extensions/AiForm.utils';
import {
    AiTabInterface,
    buildSipContactFromService,
    getSipStaticContactService,
    isSipStaticContactOn,
} from './AiTab.utils';

// ===== Main Component =====
const AiTab: React.FC<AiTabInterface> = ({
    handleInitDataChange,
    handleSubmitForm,
    handleDirtyChange,
    handleSetSubmitFunc,
    handleSetIsValidFunc,
}) => {
    // ===== Hooks =====
    const dispatch = useDispatch();

    const adapterHealth = useSelector((s: ReduxState) => s.aiAgent.adapterHealth);
    const adapterHealthLoading = useSelector((s: ReduxState) => s.aiAgent.adapterHealthLoading);
    const adapterHealthError = useSelector((s: ReduxState) => s.aiAgent.adapterHealthError);
    const voiceAiConfig = useSelector((s: ReduxState) => s.aiAgent.voiceAiConfig);
    const voiceAiConfigLoading = useSelector((s: ReduxState) => s.aiAgent.voiceAiConfigLoading);
    const tenant = useSelector((s: ReduxState) => s.aiAgent.tenant);
    const tenantLoading = useSelector((s: ReduxState) => s.aiAgent.tenantLoading);
    const tenantError = useSelector((s: ReduxState) => s.aiAgent.tenantError);
    const matchedAiAgent = useSelector((s: ReduxState) => s.aiAgent.matchedAiAgent);
    const matchedAiAgentLoading = useSelector(
        (s: ReduxState) => s.aiAgent.matchedAiAgentLoading,
    );
    const features = useSelector((s: ReduxState) => s.extensions.serviceFeatures);

    const globalEmail = useSelector(
        (s: ReduxState) => s.generic.globalCustomerInfo?.customer_info.email || null,
    );
    const globalCountry = useSelector(
        (s: ReduxState) => s.generic.globalCustomerInfo?.customer_info.country || null,
    );

    const [formAiEnabled, setFormAiEnabled] = useState<boolean | undefined>(undefined);

    // ===== Memoized Values =====
    // Feature-flag gate: drives data fetching, not the final toggle state.
    const aiEnabledValue = useMemo(() => isSipStaticContactOn(features), [features]);

    const initValues = useMemo(() => {
        const service = getSipStaticContactService(features);
        const enabled = isSipStaticContactOn(features);
        const matchedAgent = matchedAiAgent ?? null;
        return {
            init: {
                aiAgent: matchedAgent,
                aiEnabled: Boolean(matchedAgent && enabled),
            } as AiFormType,
            data: {
                aiAgentService: matchedAgent,
                sipService: service ?? null,
            },
        };
    }, [features, matchedAiAgent]);

    const effectiveAiEnabled = formAiEnabled ?? aiEnabledValue;

    const onAiEnabledChange = useCallback((enabled: boolean) => {
        setFormAiEnabled(enabled);
    }, []);

    const tenantMatchesActiveAdapter = useMemo(() => {
        const activeName = adapterHealth?.service_name;
        if (!activeName || adapterHealth?.status !== 'OK') return false;
        return Boolean(voiceAiConfig?.services?.includes(activeName));
    }, [voiceAiConfig, adapterHealth]);

    const adapterAvailable = adapterHealth?.status === 'OK';

    // ===== Effects =====
    // Fetch health + config once per tab open. The tab subtree is unmounted and
    // remounted while ExtensionDetails toggles isFormDataLoading, so this effect
    // can run more than once — guard on store state to dispatch a single request.
    // !adapterHealthError prevents a retry loop after a failed health call
    // (ICAP-2929: health is never re-fetched). ExtensionDetails resets this slice
    // on unmount / extension change, so a fresh visit fetches again.
    useEffect(() => {
        if (adapterHealth === undefined && !adapterHealthLoading && !adapterHealthError) {
            dispatch(actions.getAdapterHealth.request());
        }
        if (voiceAiConfig === undefined && !voiceAiConfigLoading) {
            dispatch(actions.getVoiceAiConfig.request());
        }
    }, [
        dispatch,
        adapterHealth,
        adapterHealthLoading,
        adapterHealthError,
        voiceAiConfig,
        voiceAiConfigLoading,
    ]);

    useEffect(() => {
        if (
            !effectiveAiEnabled ||
            !adapterAvailable ||
            tenant ||
            tenantLoading ||
            tenantError
        ) {
            return;
        }
        const isReturningCustomer =
            tenantMatchesActiveAdapter && Boolean(globalEmail) && Boolean(globalCountry);
        const isManualEnable = formAiEnabled === true;
        if (isReturningCustomer || isManualEnable) {
            dispatch(actions.refreshTenant.request());
        }
    }, [
        effectiveAiEnabled,
        adapterAvailable,
        tenantMatchesActiveAdapter,
        globalEmail,
        globalCountry,
        tenantLoading,
        tenant,
        tenantError,
        formAiEnabled,
        dispatch,
    ]);

    useEffect(() => {
        if (
            tenant?.service_name &&
            !voiceAiConfig?.services?.includes(tenant.service_name)
        ) {
            dispatch(actions.updateVoiceAiConfig.request({service_name: tenant.service_name}));
        }
    }, [tenant, voiceAiConfig, dispatch]);

    useEffect(() => {
        const service = getSipStaticContactService(features);
        const sipUri = service ? buildSipContactFromService(service) : null;
        if (
            effectiveAiEnabled &&
            adapterAvailable &&
            tenant &&
            sipUri &&
            matchedAiAgent === undefined &&
            !matchedAiAgentLoading
        ) {
            dispatch(actions.findAiAgentBySipUri.request({sipUri}));
        }
    }, [
        effectiveAiEnabled,
        adapterAvailable,
        tenant,
        features,
        matchedAiAgent,
        matchedAiAgentLoading,
        dispatch,
    ]);

    const isInitialLoading = useMemo(() => {
        if (!effectiveAiEnabled) return false;
        if (adapterHealthLoading) return true;
        if (voiceAiConfigLoading) return true;
        if (tenantLoading && !tenant) return true;
        if (matchedAiAgentLoading) return true;
        return !!(tenantMatchesActiveAdapter &&
            !tenant &&
            !tenantError &&
            globalEmail &&
            globalCountry);

    }, [
        effectiveAiEnabled,
        adapterHealthLoading,
        voiceAiConfigLoading,
        tenantLoading,
        matchedAiAgentLoading,
        tenantMatchesActiveAdapter,
        tenant,
        tenantError,
        globalEmail,
        globalCountry,
    ]);

    // Gate on the company email only — an individual's own email must not enable the toggle,
    // because the company email is what gets sent to Apifonica at the saga stage.
    const reqEmailExist = useMemo(() => {
        const hasEmail = globalEmail && globalEmail.length > 0;
        return hasEmail ? true : null;
    }, [globalEmail]);

    const reqCountryExist = useMemo(
        () => (globalCountry && globalCountry.length > 0) || false,
        [globalCountry],
    );

    // ===== Validation Schema =====
    const aiFormValidationSchema = () =>
        Yup.object().shape({
            aiAgent: Yup.object().when('aiEnabled', {
                is: true,
                then: Yup.object().required('This field is required').nullable(),
                otherwise: Yup.object().nullable(),
            }),
            aiEnabled: Yup.boolean().required(),
        });

    // ===== Init Form Values Effect =====
    useEffect(() => {
        if (!isInitialLoading && initValues.init) {
            handleInitDataChange?.(initValues.init);
            handleSubmitForm?.(initValues.init);
        }
    }, [isInitialLoading, initValues, handleInitDataChange, handleSubmitForm]);

    // ===== Render =====
    return (
        <Formik<AiFormType>
            initialValues={initValues.init}
            onSubmit={(form) => handleSubmitForm?.(form)}
            validationSchema={aiFormValidationSchema}
            validateOnChange={false}
            enableReinitialize
        >
            {() => (
                <AiForm
                    emailIsPresent={reqEmailExist}
                    countryIsPresent={reqCountryExist}
                    adapterAvailable={adapterAvailable}
                    initSessionFailed={Boolean(tenantError)}
                    isInitialLoading={isInitialLoading}
                    onAiEnabledChange={onAiEnabledChange}
                    handleSubmitForm={handleSubmitForm}
                    handleDirtyChange={handleDirtyChange}
                    handleSetSubmitFunc={handleSetSubmitFunc}
                    handleSetIsValidFunc={handleSetIsValidFunc}
                />
            )}
        </Formik>
    );
};

// ===== Export =====
export default AiTab;
